import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { 
  ShoppingCart, 
  Plus, 
  Search, 
  Filter, 
  CalendarIcon, 
  DollarSign, 
  TrendingUp, 
  Eye,
  Download,
  Loader2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';
import { useLocation } from '@/contexts/LocationContext';
import type { Database } from '@/integrations/supabase/types';

// Database types
type SaleRow = Database['public']['Tables']['sales']['Row'];
type CustomerRow = Database['public']['Tables']['customers']['Row'];
type UserProfileRow = Database['public']['Tables']['user_profiles']['Row'];

interface Sale {
  id: string; // Group ID (for grouping purposes)
  date: Date;
  customerName: string;
  customerPhone: string;
  customerId?: string | null; // Customer ID for reliable grouping
  total: number;
  paymentStatus: 'paid' | 'partial' | 'vadeli' | 'overdue';
  paymentType: Database['public']['Enums']['payment_type_enum'] | null;
  lineCount: number;
  salesPerson: string;
  status: Database['public']['Enums']['sale_status_enum'];
  balanceDue: number;
  totalPaid: number;
  productNames: string[];
  isOnHold?: boolean; // Flag to indicate if this is from on_holds table
  linkedSaleId?: string | null; // For onhold records linked to a sale
  originalIds?: string[]; // Track original IDs for grouped records
  actualSaleId?: string; // The actual sale ID to use for navigation (if different from id)
  isCompletedOrConvertedOnHold?: boolean; // Flag to indicate if this onhold is completed or converted
}

// Helper functions
const determinePaymentStatus = (sale: SaleRow, paymentType: Database['public']['Enums']['payment_type_enum'] | null): 'paid' | 'partial' | 'vadeli' | 'overdue' => {
  // If fully paid, show as paid
  if (sale.total_paid >= sale.total_net && sale.balance_due <= 0) return 'paid';
  
  // If payment type is promissory (vadeli) or there's a balance due, it's vadeli
  if (paymentType === 'promissory' || sale.balance_due > 0) {
    // Check if overdue based on date
    const daysSinceSale = (new Date().getTime() - new Date(sale.sale_datetime).getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceSale > 30 ? 'overdue' : 'vadeli';
  }
  
  // Partial payment
  if (sale.total_paid > 0) return 'partial';
  
  // Default to vadeli for unpaid sales
  return 'vadeli';
};



const paymentStatusConfig = {
  paid: { label: 'Ödendi', color: 'bg-success/20 text-success-foreground border-success/30' },
  partial: { label: 'Kısmi', color: 'bg-warning/20 text-warning-foreground border-warning/30' },
  vadeli: { label: 'Vadeli', color: 'bg-primary/20 text-primary-foreground border-primary/30' },
  overdue: { label: 'Vadesi Geçmiş', color: 'bg-destructive/20 text-destructive-foreground border-destructive/30' }
};

const saleStatusConfig: Record<Database['public']['Enums']['sale_status_enum'], { label: string; color: string; bgColor: string; borderColor: string }> = {
  open: { 
    label: 'Beklemede', 
    color: 'text-yellow-600 dark:text-yellow-500',
    bgColor: 'bg-yellow-50 dark:bg-yellow-950/30',
    borderColor: 'border-yellow-200 dark:border-yellow-800'
  },
  completed: { 
    label: 'Tamamlandı', 
    color: 'text-success',
    bgColor: 'bg-success/5',
    borderColor: 'border-success/20'
  },
  cancelled: { 
    label: 'İptal Edildi', 
    color: 'text-destructive',
    bgColor: 'bg-destructive/5',
    borderColor: 'border-destructive/20'
  },
  refunded: { 
    label: 'İade Edildi', 
    color: 'text-warning',
    bgColor: 'bg-warning/5',
    borderColor: 'border-warning/20'
  }
};

const paymentTypeConfig: Record<Database['public']['Enums']['payment_type_enum'], { label: string; icon: any }> = {
  cash: { label: 'Nakit', icon: DollarSign },
  credit_card: { label: 'Kredi Kartı', icon: DollarSign },
  odero: { label: 'Odero', icon: TrendingUp },
  paro: { label: 'Paro', icon: TrendingUp },
  transfer: { label: 'Transfer', icon: TrendingUp },
  promissory: { label: 'Senet', icon: TrendingUp },
  other: { label: 'Diğer', icon: DollarSign }
};

export default function Sales() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { companyId, loading: authLoading } = useAuth();
  const { currentLocation } = useLocation();
  
  
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>('all');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({});
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [sales, setSales] = useState<Sale[]>([]);
  const [allSearchResults, setAllSearchResults] = useState<Sale[]>([]); // Store all search results for pagination
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const pageSize = 50; // Results per page for search

  // Fetch sales data from database with pagination and search
  const fetchSales = async (page: number = 1, search: string = '') => {
    if (!companyId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const searchTermTrimmed = search.trim();
      const hasSearch = searchTermTrimmed.length > 0;
      const offset = (page - 1) * pageSize;
      
      // Fetch sales with search filtering
      let salesData: any[] = [];
      let salesPage = 0;
      const fetchPageSize = 1000; // Internal pagination for fetching all matching records
      let salesHasMore = true;
      // When searching, we need to fetch more records to filter client-side
      // Since we can't search joined tables directly, we fetch recent records and filter
      const salesFetchLimit = hasSearch ? 2000 : undefined; // Fetch more when searching to find matches

      while (salesHasMore && (!salesFetchLimit || salesData.length < salesFetchLimit)) {
        let salesQuery = supabase
          .from('sales')
          .select(`
            *,
            customers(id, first_name, last_name, phone),
            sale_items(
              products(name)
            )
          `)
          .eq('company_id', companyId);

        if (currentLocation?.id) {
          salesQuery = salesQuery.eq('location_id', currentLocation.id);
        }
        
        // When searching, we fetch recent records and filter client-side
        // This ensures we catch matches in customer name/phone even if ID doesn't match
        salesQuery = salesQuery
          .order('sale_datetime', { ascending: false })
          .range(salesPage * fetchPageSize, (salesPage + 1) * fetchPageSize - 1);
        
        const { data: pageData, error: salesError } = await salesQuery;
        
        if (salesError) throw salesError;

        if (pageData && pageData.length > 0) {
          salesData = [...salesData, ...pageData];
          salesPage++;
          salesHasMore = pageData.length === fetchPageSize;
        } else {
          salesHasMore = false;
        }
      }
      
      // Apply client-side search filtering for sales (searches in customer fields and ID)
      if (hasSearch && salesData.length > 0) {
        const searchLower = searchTermTrimmed.toLowerCase();
        salesData = salesData.filter((sale: any) => {
          const customer = sale.customers;
          const customerName = customer 
            ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim().toLowerCase()
            : '';
          const customerPhone = customer?.phone || '';
          
          return (
            customerName.includes(searchLower) ||
            customerPhone.includes(searchTermTrimmed) ||
            sale.id.toLowerCase().includes(searchLower)
          );
        });
      }
      
      // Fetch onhold records with search filtering
      let onHoldData: any[] = [];
      let onHoldPage = 0;
      let onHoldHasMore = true;
      // When searching, fetch more records to filter client-side
      const onHoldFetchLimit = hasSearch ? 2000 : undefined; // Fetch more when searching to find matches

      while (onHoldHasMore && (!onHoldFetchLimit || onHoldData.length < onHoldFetchLimit)) {
        let onHoldQuery = supabase
          .from('on_holds')
          .select(`
            *,
            customer:customers(id, first_name, last_name, phone),
            product:products(name)
          `)
          .eq('company_id', companyId)
          .eq('status', 'on_hold');

        if (currentLocation?.id) {
          onHoldQuery = onHoldQuery.eq('location_id', currentLocation.id);
        }
        
        // When searching, we fetch recent records and filter client-side
        onHoldQuery = onHoldQuery
          .order('date_on_hold', { ascending: false })
          .range(onHoldPage * fetchPageSize, (onHoldPage + 1) * fetchPageSize - 1);
        
        const { data: pageData, error: onHoldError } = await onHoldQuery;
        
        if (onHoldError) throw onHoldError;

        if (pageData && pageData.length > 0) {
          onHoldData = [...onHoldData, ...pageData];
          onHoldPage++;
          onHoldHasMore = pageData.length === fetchPageSize;
        } else {
          onHoldHasMore = false;
        }
      }
      
      // Apply client-side search filtering for onholds (searches in customer, product, and ID fields)
      if (hasSearch && onHoldData.length > 0) {
        const searchLower = searchTermTrimmed.toLowerCase();
        onHoldData = onHoldData.filter((item: any) => {
          const customer = item.customer;
          const product = item.product;
          const customerName = customer 
            ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim().toLowerCase()
            : '';
          const customerPhone = customer?.phone || '';
          const productName = product?.name?.toLowerCase() || '';
          
          return (
            customerName.includes(searchLower) ||
            customerPhone.includes(searchTermTrimmed) ||
            productName.includes(searchLower) ||
            item.id.toLowerCase().includes(searchLower)
          );
        });
      }

      // Fetch converted onhold records to identify sales linked to onhold conversions
      // This helps us filter out 0 TL sales created during onhold conversion
      // Also fetch full details to display converted/completed onholds
      let convertedOnHoldData: any[] = [];
      let convertedOnHoldPage = 0;
      let convertedOnHoldHasMore = true;

      while (convertedOnHoldHasMore) {
        let convertedOnHoldQuery = supabase
          .from('on_holds')
          .select(`
            *,
            customer:customers(id, first_name, last_name, phone),
            product:products(name)
          `)
          .eq('company_id', companyId)
          .eq('status', 'converted')
          .not('linked_sale_id', 'is', null);

        if (currentLocation?.id) {
          convertedOnHoldQuery = convertedOnHoldQuery.eq('location_id', currentLocation.id);
        }

        const { data: pageData, error: convertedOnHoldError } = await convertedOnHoldQuery
          .range(convertedOnHoldPage * fetchPageSize, (convertedOnHoldPage + 1) * fetchPageSize - 1);
        
        if (convertedOnHoldError) throw convertedOnHoldError;

        if (pageData && pageData.length > 0) {
          convertedOnHoldData = [...convertedOnHoldData, ...pageData];
          convertedOnHoldPage++;
          convertedOnHoldHasMore = pageData.length === fetchPageSize;
        } else {
          convertedOnHoldHasMore = false;
        }
      }

      // Create a set of sale IDs that are linked to onhold conversions
      const onHoldLinkedSaleIds = new Set(
        convertedOnHoldData
          .map((onHold: any) => onHold.linked_sale_id)
          .filter(Boolean)
      );

      // Fetch linked sale statuses to determine if converted onholds are completed
      const linkedSaleIds = Array.from(onHoldLinkedSaleIds);
      const linkedSaleStatusMap = new Map<string, Database['public']['Enums']['sale_status_enum']>();
      
      if (linkedSaleIds.length > 0) {
        // Batch fetch sale statuses
        const batchSize = 50;
        for (let i = 0; i < linkedSaleIds.length; i += batchSize) {
          const batchIds = linkedSaleIds.slice(i, i + batchSize);
          let linkedSalesQuery = supabase
            .from('sales')
            .select('id, status')
            .in('id', batchIds);

          if (currentLocation?.id) {
            linkedSalesQuery = linkedSalesQuery.eq('location_id', currentLocation.id);
          }

          const { data: linkedSales } = await linkedSalesQuery;
          
          if (linkedSales) {
            linkedSales.forEach(sale => {
              linkedSaleStatusMap.set(sale.id, sale.status);
            });
          }
        }
      }
      
      if (salesData) {
        // Batch fetch all payment types for all sales (much more efficient)
        const saleIds = salesData.map(s => s.id);
        
        // Batch fetch payments to avoid URL length limits
        // Using smaller batch size (50) to stay well under URL limits
        const batchSize = 50;
        const allPayments: any[] = [];
        
        for (let i = 0; i < saleIds.length; i += batchSize) {
          const batchIds = saleIds.slice(i, i + batchSize);
          const { data: batchPayments } = await supabase
            .from('sale_payments')
            .select('sale_id, payment_type, paid_at')
            .in('sale_id', batchIds)
            .order('paid_at', { ascending: false });
          
          if (batchPayments) {
            allPayments.push(...batchPayments);
          }
        }
        
        // Create a map of sale_id -> most recent payment type
        const paymentTypeMap = new Map<string, any>();
        if (allPayments.length > 0) {
          // Group by sale_id and find the most recent payment for each sale
          const paymentsBySale = new Map<string, any[]>();
          allPayments.forEach(payment => {
            if (!paymentsBySale.has(payment.sale_id)) {
              paymentsBySale.set(payment.sale_id, []);
            }
            paymentsBySale.get(payment.sale_id)!.push(payment);
          });
          
          paymentsBySale.forEach((payments, saleId) => {
            // Sort by paid_at desc to get most recent, then take first
            const sorted = payments.sort((a, b) => {
              const dateA = new Date(a.paid_at || 0).getTime();
              const dateB = new Date(b.paid_at || 0).getTime();
              return dateB - dateA;
            });
            paymentTypeMap.set(saleId, sorted[0]?.payment_type || null);
          });
        }
        
        // Create a map of sale_id -> line item count
        // First, count from the sale_items already fetched in the query
        const lineCountMap = new Map<string, number>();
        salesData.forEach(sale => {
          const itemCount = (sale.sale_items || []).length;
          if (itemCount > 0) {
            lineCountMap.set(sale.id, itemCount);
          }
        });
        
        // Batch fetch all line item counts for all sales (as a more accurate source)
        // This handles cases where sale_items might not be fully loaded in the initial query
        if (saleIds.length > 0) {
          // Supabase .in() has a limit, and URL length limits mean we need smaller batches
          // Each UUID is ~36 chars, so 50 UUIDs = ~1800 chars + query overhead
          // Using 50 to stay well under typical 2048-8192 char URL limits
          const batchSize = 50;
          for (let i = 0; i < saleIds.length; i += batchSize) {
            const batchIds = saleIds.slice(i, i + batchSize);
            const { data: allSaleItems } = await supabase
              .from('sale_items')
              .select('sale_id, id')
              .in('sale_id', batchIds);
            
            if (allSaleItems) {
              const countsBySale = new Map<string, number>();
              allSaleItems.forEach(item => {
                countsBySale.set(item.sale_id, (countsBySale.get(item.sale_id) || 0) + 1);
              });
              countsBySale.forEach((count, saleId) => {
                // Use the batch query result if it's higher (more accurate)
                const existingCount = lineCountMap.get(saleId) || 0;
                lineCountMap.set(saleId, Math.max(count, existingCount));
              });
            }
          }
        }
        
        // Batch fetch all user profiles for unique created_by IDs
        const uniqueUserIds = [...new Set(salesData.map(s => s.created_by).filter(Boolean))] as string[];
        const userProfileMap = new Map<string, string>();
        
        if (uniqueUserIds.length > 0) {
          const { data: userProfiles } = await supabase
            .from('user_profiles')
            .select('id, full_name')
            .in('id', uniqueUserIds);
          
          if (userProfiles) {
            userProfiles.forEach(profile => {
              userProfileMap.set(profile.id, profile.full_name || 'Bilinmeyen');
            });
          }
        }
        
        // Transform database sales to Sale interface (no async operations needed now)
        // Filter out 0 TL sales that are linked to onhold conversions (these are inventory adjustment records)
        const transformedSales: Sale[] = salesData
          .filter((sale) => {
            // Exclude sales with total_net = 0 (or very close to 0) that are linked to onhold conversions
            const isZeroAmount = Math.abs(sale.total_net || 0) < 0.01;
            const isLinkedToOnHold = onHoldLinkedSaleIds.has(sale.id);
            
            // Filter out if it's a zero amount sale linked to onhold conversion
            if (isZeroAmount && isLinkedToOnHold) {
              return false;
            }
            return true;
          })
          .map((sale) => {
            const customer = sale.customers;
            const paymentType = paymentTypeMap.get(sale.id) || null;
            
            // Get line count from map, or fallback to counting sale_items directly
            const mappedLineCount = lineCountMap.get(sale.id);
            const directLineCount = (sale.sale_items || []).length;
            const lineCount = mappedLineCount !== undefined ? mappedLineCount : (directLineCount > 0 ? directLineCount : 0);
            
            const salesPerson = sale.created_by ? (userProfileMap.get(sale.created_by) || 'Bilinmeyen') : 'Bilinmeyen';
            
            // Extract product names from sale items
            const productNames = (sale.sale_items || [])
              .map(item => item.products?.name)
              .filter(name => name)
              .slice(0, 3); // Show only first 3 products to avoid too long text

            return {
              id: sale.id,
              date: new Date(sale.sale_datetime),
              customerName: customer 
                ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Bilinmeyen Müşteri'
                : 'Bilinmeyen Müşteri',
              customerPhone: customer?.phone || '',
              customerId: sale.customer_id || customer?.id || null,
              total: sale.total_net,
              paymentStatus: determinePaymentStatus(sale, paymentType),
              paymentType,
              lineCount,
              salesPerson,
              status: sale.status,
              balanceDue: sale.balance_due,
              totalPaid: sale.total_paid,
              productNames: productNames,
              isOnHold: false,
              actualSaleId: sale.id
            };
          });
        
        // Transform onhold records to Sale interface
        const transformedOnHolds: Sale[] = (onHoldData || []).map((onHold: any) => {
          const customer = onHold.customer;
          const product = onHold.product;
          
          // For grouping: use linked_sale_id if exists, otherwise use a unique prefix + onhold id
          // This ensures onhold records without linked_sale_id are grouped separately
          const groupId = onHold.linked_sale_id || `onhold_${onHold.id}`;
          
          return {
            id: groupId,
            date: new Date(onHold.date_on_hold),
            customerName: customer 
              ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Bilinmeyen Müşteri'
              : 'Bilinmeyen Müşteri',
            customerPhone: customer?.phone || '',
            customerId: onHold.customer_id || customer?.id || null,
            total: parseFloat(onHold.hold_price || 0) * (onHold.hold_quantity || 1),
            paymentStatus: 'pending' as const,
            paymentType: null,
            lineCount: 1,
            salesPerson: 'Bilinmeyen',
            status: 'open' as Database['public']['Enums']['sale_status_enum'],
            balanceDue: parseFloat(onHold.hold_price || 0) * (onHold.hold_quantity || 1),
            totalPaid: 0,
            productNames: product?.name ? [product.name] : [],
            isOnHold: true,
            linkedSaleId: onHold.linked_sale_id,
            originalIds: [onHold.id],
            actualSaleId: onHold.linked_sale_id || onHold.id,
            isCompletedOrConvertedOnHold: false // Regular onhold records
          };
        });

        // Transform converted onhold records to Sale interface
        const transformedConvertedOnHolds: Sale[] = (convertedOnHoldData || []).map((onHold: any) => {
          const customer = onHold.customer;
          const product = onHold.product;
          const linkedSaleStatus = onHold.linked_sale_id ? linkedSaleStatusMap.get(onHold.linked_sale_id) : null;
          const isCompleted = linkedSaleStatus === 'completed';
          
          // For grouping: use linked_sale_id since converted onholds always have one
          const groupId = onHold.linked_sale_id || `onhold_${onHold.id}`;
          
          return {
            id: groupId,
            date: new Date(onHold.date_on_hold),
            customerName: customer 
              ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Bilinmeyen Müşteri'
              : 'Bilinmeyen Müşteri',
            customerPhone: customer?.phone || '',
            customerId: onHold.customer_id || customer?.id || null,
            total: parseFloat(onHold.hold_price || 0) * (onHold.hold_quantity || 1),
            paymentStatus: 'pending' as const,
            paymentType: null,
            lineCount: 1,
            salesPerson: 'Bilinmeyen',
            status: linkedSaleStatus || ('open' as Database['public']['Enums']['sale_status_enum']),
            balanceDue: parseFloat(onHold.hold_price || 0) * (onHold.hold_quantity || 1),
            totalPaid: 0,
            productNames: product?.name ? [product.name] : [],
            isOnHold: true,
            linkedSaleId: onHold.linked_sale_id,
            originalIds: [onHold.id],
            actualSaleId: onHold.linked_sale_id || onHold.id,
            isCompletedOrConvertedOnHold: true // Mark as completed/converted onhold
          };
        });
        
        // Merge sales, onholds, and converted onholds
        const allRecords = [...transformedSales, ...transformedOnHolds, ...transformedConvertedOnHolds];
        
        // Helper function to get date key (same day, ignoring time)
        const getDateKey = (date: Date): string => {
          return format(date, 'yyyy-MM-dd');
        };
        
        // Helper function to normalize phone number (remove spaces, dashes, etc.)
        const normalizePhone = (phone: string): string => {
          if (!phone) return '';
          return phone.replace(/\s+/g, '').replace(/[-\+]/g, '').trim();
        };
        
        // Helper function to normalize customer name (lowercase, trim)
        const normalizeName = (name: string): string => {
          if (!name) return '';
          return name.toLowerCase().trim();
        };
        
        // Helper function to get customer key (prefer customer_id, fallback to normalized phone/name)
        const getCustomerKey = (record: Sale): string => {
          // First priority: use customer_id if available
          if (record.customerId) {
            return `customer_${record.customerId}`;
          }
          // Second priority: use normalized phone number
          const normalizedPhone = normalizePhone(record.customerPhone);
          if (normalizedPhone) {
            return `phone_${normalizedPhone}`;
          }
          // Last resort: use normalized name
          const normalizedName = normalizeName(record.customerName);
          return normalizedName || 'unknown';
        };
        
        // Group records by customer + date (same day) + status
        // This ensures cancelled sales are shown separately from completed sales
        const groupedRecords = new Map<string, Sale>();
        
        allRecords.forEach(record => {
          // Create grouping key: customer + date (same day) + status
          // This way cancelled sales won't be merged with completed sales
          const dateKey = getDateKey(record.date);
          const customerKey = getCustomerKey(record);
          const statusKey = record.status || 'unknown';
          const groupKey = `${customerKey}_${dateKey}_${statusKey}`;
          
          if (groupedRecords.has(groupKey)) {
            // Merge with existing record
            const existing = groupedRecords.get(groupKey)!;
            
            // Merge product names (avoid duplicates)
            const allProductNames = [...existing.productNames, ...record.productNames];
            const uniqueProductNames = Array.from(new Set(allProductNames));
            
            // Update totals
            // Note: Cancelled sales totals are shown for display but excluded from stats calculations
            existing.total += record.total;
            existing.balanceDue += record.balanceDue;
            existing.totalPaid += record.totalPaid;
            existing.lineCount += record.lineCount;
            existing.productNames = uniqueProductNames;
            
            // Keep the most recent date/time
            if (record.date > existing.date) {
              existing.date = record.date;
            }
            
            // If either is onhold, mark as onhold
            if (record.isOnHold) {
              existing.isOnHold = true;
            }
            
            // If either is completed/converted onhold, mark as such
            if (record.isCompletedOrConvertedOnHold) {
              existing.isCompletedOrConvertedOnHold = true;
            }
            
            // Merge linked sale IDs (keep the first non-null one)
            if (record.linkedSaleId && !existing.linkedSaleId) {
              existing.linkedSaleId = record.linkedSaleId;
              // Update actualSaleId if we have a linked sale
              existing.actualSaleId = record.linkedSaleId;
            }
            
            // Track original IDs
            if (record.originalIds) {
              existing.originalIds = [...(existing.originalIds || []), ...record.originalIds];
            } else {
              existing.originalIds = [...(existing.originalIds || []), record.id];
            }
            
            // Update actualSaleId to the linked sale if available, otherwise keep the first one
            if (record.actualSaleId && !record.actualSaleId.startsWith('onhold_')) {
              existing.actualSaleId = record.actualSaleId;
            }
            
            // Update payment status to reflect the worst status (overdue > pending > partial > paid)
            const statusPriority = { overdue: 4, pending: 3, partial: 2, paid: 1 };
            if (statusPriority[record.paymentStatus] > statusPriority[existing.paymentStatus]) {
              existing.paymentStatus = record.paymentStatus;
            }
            
            // Update payment type to the most recent one
            if (record.paymentType) {
              existing.paymentType = record.paymentType;
            }
            
            // Preserve customerId if available (prefer existing if both have it)
            if (record.customerId && !existing.customerId) {
              existing.customerId = record.customerId;
            }
          } else {
            // First record with this customer+date combination
            groupedRecords.set(groupKey, {
              ...record,
              originalIds: record.originalIds || [record.id],
              actualSaleId: record.actualSaleId || record.id
            });
          }
        });
        
        // Convert map to array and sort by date (most recent first)
        const groupedArray = Array.from(groupedRecords.values());
        groupedArray.sort((a, b) => b.date.getTime() - a.date.getTime());
        
        // Store grouped results
        setTotalResults(groupedArray.length);
        
        // Apply pagination if searching
        if (hasSearch) {
          // Store all results for client-side pagination
          setAllSearchResults(groupedArray);
          const startIndex = offset;
          const endIndex = offset + pageSize;
          const paginatedResults = groupedArray.slice(startIndex, endIndex);
          setSales(paginatedResults);
          setHasMoreResults(endIndex < groupedArray.length);
        } else {
          // No search: show all results, clear search results cache
          setAllSearchResults([]);
          setSales(groupedArray);
          setHasMoreResults(false);
        }
      }
    } catch (err) {
      console.error('Error fetching sales:', err);
      setError('Satışlar yüklenirken bir hata oluştu');
      toast({
        title: "Hata",
        description: "Satışlar yüklenirken bir hata oluştu",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Debounce search to avoid too many API calls
  useEffect(() => {
    if (!companyId || authLoading) return;
    
    const timeoutId = setTimeout(() => {
      setCurrentPage(1); // Reset to first page on new search
      void fetchSales(1, searchTerm);
    }, searchTerm.trim().length > 0 ? 500 : 0); // Debounce only when searching
    
    return () => clearTimeout(timeoutId);
  }, [companyId, authLoading, searchTerm, currentLocation?.id]);

  // Handle pagination for search results (client-side pagination from stored results)
  useEffect(() => {
    if (searchTerm.trim() && allSearchResults.length > 0) {
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedResults = allSearchResults.slice(startIndex, endIndex);
      setSales(paginatedResults);
      setHasMoreResults(endIndex < allSearchResults.length);
    }
  }, [currentPage, searchTerm, allSearchResults]);

  // Fetch data on mount or when filters change (but not search - that's handled above)
  useEffect(() => {
    if (companyId && !authLoading && !searchTerm.trim()) {
      void fetchSales(1, '');
    }
  }, [companyId, authLoading, currentLocation?.id]);

  const filteredSales = useMemo(() => {
    const hasSearch = searchTerm.trim().length > 0;
    
    return sales.filter(sale => {
      // Search filtering is already done at database level when hasSearch is true
      // So we skip client-side search filtering - matchesSearch should always be true
      // to allow other filters (payment status, payment type, date range) to work correctly
      const matchesSearch = true; // Search already filtered at DB level, so always match here
      
      const matchesPaymentStatus = paymentStatusFilter === 'all' || sale.paymentStatus === paymentStatusFilter;
      const matchesPaymentType = paymentTypeFilter === 'all' || sale.paymentType === paymentTypeFilter;
      
      // Date range filtering: compare dates only (ignore time)
      let matchesDateRange = true;
      if (dateRange.from || dateRange.to) {
        if (dateRange.from && dateRange.to) {
          // Both dates selected: check if sale date is within range
          const saleDateOnly = new Date(sale.date.getFullYear(), sale.date.getMonth(), sale.date.getDate());
          const fromDateOnly = new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), dateRange.from.getDate());
          const toDateOnly = new Date(dateRange.to.getFullYear(), dateRange.to.getMonth(), dateRange.to.getDate());
          matchesDateRange = saleDateOnly >= fromDateOnly && saleDateOnly <= toDateOnly;
        } else if (dateRange.from) {
          // Only start date selected: check if sale date is on or after start date
          const saleDateOnly = new Date(sale.date.getFullYear(), sale.date.getMonth(), sale.date.getDate());
          const fromDateOnly = new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), dateRange.from.getDate());
          matchesDateRange = saleDateOnly >= fromDateOnly;
        } else if (dateRange.to) {
          // Only end date selected: check if sale date is on or before end date
          const saleDateOnly = new Date(sale.date.getFullYear(), sale.date.getMonth(), sale.date.getDate());
          const toDateOnly = new Date(dateRange.to.getFullYear(), dateRange.to.getMonth(), dateRange.to.getDate());
          matchesDateRange = saleDateOnly <= toDateOnly;
        }
      }
      
      return matchesSearch && matchesPaymentStatus && matchesPaymentType && matchesDateRange;
    });
  }, [sales, searchTerm, paymentStatusFilter, paymentTypeFilter, dateRange]);

  const getSalesStats = () => {
    // Exclude cancelled sales from stats calculations
    const activeSales = filteredSales.filter(sale => sale.status !== 'cancelled');
    const totalSales = activeSales.reduce((sum, sale) => sum + sale.total, 0);
    const paidSales = activeSales.filter(sale => sale.paymentStatus === 'paid');
    const pendingSales = activeSales.filter(sale => sale.paymentStatus === 'vadeli' || sale.paymentStatus === 'overdue');
    
    return {
      totalAmount: totalSales,
      totalCount: activeSales.length,
      paidCount: paidSales.length,
      pendingCount: pendingSales.length,
      averageSale: activeSales.length > 0 ? totalSales / activeSales.length : 0
    };
  };

  const stats = getSalesStats();

  const handleExport = () => {
    toast({
      title: "Dışa Aktarma Başladı",
      description: "Satış verisi dışa aktarımı hazırlanıyor...",
    });
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Satışlar</h1>
              <p className="text-sm sm:text-base text-muted-foreground">Satış geçmişi ve takibi</p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExport}>
              <Download className="w-4 h-4 mr-2" />
              Dışa Aktar
            </Button>
            <Button 
              className="gap-2 w-full sm:w-auto"
              onClick={() => navigate('/sales/new')}
            >
              <Plus className="w-4 h-4" />
              Yeni Satış
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold">₺{stats.totalAmount.toLocaleString('tr-TR')}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">Toplam Satış</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-success/10 flex items-center justify-center">
                  <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5 text-success" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold">{stats.totalCount}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">Toplam Sipariş</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-warning/10 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-warning" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold">₺{stats.averageSale.toLocaleString('tr-TR')}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">Ortalama Satış</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                  <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-accent" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold">{stats.pendingCount}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">Beklemede</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filtreler
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Search */}
              <div>
                <Label htmlFor="search">Ara</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    id="search"
                    placeholder="Müşteri, telefon veya ID ile ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Date Range */}
              <div>
                <Label>Tarih Aralığı</Label>
                <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateRange.from ? (
                        dateRange.to ? (
                          <>
                            {format(dateRange.from, "d MMM yyyy", { locale: tr })} - {format(dateRange.to, "d MMM yyyy", { locale: tr })}
                          </>
                        ) : (
                          format(dateRange.from, "d MMM yyyy", { locale: tr })
                        )
                      ) : (
                        <span>Tarih aralığı seç</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <div className="p-4">
                      {/* Quick Actions - Organized in groups */}
                      <div className="space-y-3 mb-4">
                        {/* Common Periods */}
                        <div>
                          <div className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Hızlı Seçim</div>
                          <div className="grid grid-cols-2 gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-sm h-9 justify-start"
                              onClick={() => {
                                const today = new Date();
                                const startOfWeek = new Date(today);
                                startOfWeek.setDate(today.getDate() - today.getDay());
                                startOfWeek.setHours(0, 0, 0, 0);
                                setDateRange({ from: startOfWeek, to: today });
                                setTimeout(() => setShowDatePicker(false), 100);
                              }}
                            >
                              Bu Hafta
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-sm h-9 justify-start"
                              onClick={() => {
                                const today = new Date();
                                const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                                setDateRange({ from: startOfMonth, to: today });
                                setTimeout(() => setShowDatePicker(false), 100);
                              }}
                            >
                              Bu Ay
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-sm h-9 justify-start"
                              onClick={() => {
                                const today = new Date();
                                const startOfYear = new Date(today.getFullYear(), 0, 1);
                                setDateRange({ from: startOfYear, to: today });
                                setTimeout(() => setShowDatePicker(false), 100);
                              }}
                            >
                              Bu Yıl
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-sm h-9 justify-start"
                              onClick={() => {
                                setDateRange({});
                                setTimeout(() => setShowDatePicker(false), 100);
                              }}
                            >
                              Tümü
                            </Button>
                          </div>
                        </div>

                        {/* Recent Periods */}
                        <div>
                          <div className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Son Dönemler</div>
                          <div className="grid grid-cols-2 gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-sm h-9 justify-start"
                              onClick={() => {
                                const today = new Date();
                                const threeMonthsAgo = new Date(today);
                                threeMonthsAgo.setMonth(today.getMonth() - 3);
                                setDateRange({ from: threeMonthsAgo, to: today });
                                setTimeout(() => setShowDatePicker(false), 100);
                              }}
                            >
                              Son 3 Ay
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-sm h-9 justify-start"
                              onClick={() => {
                                const today = new Date();
                                const sixMonthsAgo = new Date(today);
                                sixMonthsAgo.setMonth(today.getMonth() - 6);
                                setDateRange({ from: sixMonthsAgo, to: today });
                                setTimeout(() => setShowDatePicker(false), 100);
                              }}
                            >
                              Son 6 Ay
                            </Button>
                          </div>
                        </div>

                        {/* Year Halves */}
                        <div>
                          <div className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Yıl Yarısı</div>
                          <div className="grid grid-cols-2 gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-sm h-9 justify-start"
                              onClick={() => {
                                const today = new Date();
                                const startOfFirstHalf = new Date(today.getFullYear(), 0, 1);
                                const endOfFirstHalf = new Date(today.getFullYear(), 5, 30);
                                setDateRange({ from: startOfFirstHalf, to: endOfFirstHalf });
                                setTimeout(() => setShowDatePicker(false), 100);
                              }}
                            >
                              İlk 6 Ay
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-sm h-9 justify-start"
                              onClick={() => {
                                const today = new Date();
                                const startOfSecondHalf = new Date(today.getFullYear(), 6, 1);
                                const endOfSecondHalf = new Date(today.getFullYear(), 11, 31);
                                setDateRange({ from: startOfSecondHalf, to: endOfSecondHalf });
                                setTimeout(() => setShowDatePicker(false), 100);
                              }}
                            >
                              İkinci 6 Ay
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Calendar Separator */}
                      <div className="relative my-4">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-background px-2 text-muted-foreground font-semibold">veya</span>
                        </div>
                      </div>

                      {/* Custom Date Selection */}
                      <div>
                        <div className="text-sm font-semibold mb-3">Özel Tarih Seç</div>
                        <Calendar
                          initialFocus
                          mode="range"
                          defaultMonth={dateRange.from || new Date()}
                          selected={dateRange.from && dateRange.to ? { from: dateRange.from, to: dateRange.to } : dateRange.from ? { from: dateRange.from } : undefined}
                          onSelect={(range) => {
                            if (range) {
                              setDateRange(range);
                              // Close popover when both dates are selected
                              if (range.from && range.to) {
                                setTimeout(() => setShowDatePicker(false), 100);
                              }
                            } else {
                              setDateRange({});
                            }
                          }}
                          numberOfMonths={2}
                          className="pointer-events-auto rounded-md border"
                        />
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Payment Status */}
              <div>
                <Label>Ödeme Durumu</Label>
                <Select value={paymentStatusFilter} onValueChange={setPaymentStatusFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Durumlar</SelectItem>
                    <SelectItem value="paid">Ödendi</SelectItem>
                    <SelectItem value="partial">Kısmi</SelectItem>
                    <SelectItem value="vadeli">Vadeli</SelectItem>
                    <SelectItem value="overdue">Vadesi Geçmiş</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Payment Type */}
              <div>
                <Label>Ödeme Türü</Label>
                <Select value={paymentTypeFilter} onValueChange={setPaymentTypeFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Türler</SelectItem>
                    <SelectItem value="cash">Nakit</SelectItem>
                    <SelectItem value="credit_card">Kredi Kartı</SelectItem>
                    <SelectItem value="odero">Odero</SelectItem>
                    <SelectItem value="paro">Paro</SelectItem>
                    <SelectItem value="transfer">Transfer</SelectItem>
                    <SelectItem value="promissory">Senet</SelectItem>
                    <SelectItem value="other">Diğer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {(searchTerm || paymentStatusFilter !== 'all' || paymentTypeFilter !== 'all' || dateRange.from || dateRange.to) && (
              <div className="mt-4">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setSearchTerm('');
                    setPaymentStatusFilter('all');
                    setPaymentTypeFilter('all');
                    setDateRange({});
                    setCurrentPage(1); // Reset pagination when clearing filters
                    setAllSearchResults([]); // Clear search results cache
                  }}
                >
                  Filtreleri Temizle
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sales List */}
        <Card>
          <CardHeader>
            <CardTitle>
              Satış Geçmişi 
              {searchTerm.trim() ? (
                <span className="text-muted-foreground font-normal">
                  {' '}({filteredSales.length} / {totalResults} sonuç)
                </span>
              ) : (
                <span className="text-muted-foreground font-normal">
                  {' '}({filteredSales.length})
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Satışlar yükleniyor...</span>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <div className="text-destructive mb-4">{error}</div>
                <Button onClick={fetchSales} variant="outline">
                  Tekrar Dene
                </Button>
              </div>
            ) : filteredSales.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingCart className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2">Satış bulunamadı</h3>
                <p className="text-muted-foreground mb-4">
                  {searchTerm || paymentStatusFilter !== 'all' || paymentTypeFilter !== 'all' || dateRange.from || dateRange.to
                    ? 'Filtrelerinizi ayarlamayı deneyin'
                    : 'Başlamak için ilk satışınızı oluşturun'
                  }
                </p>
                {!searchTerm && paymentStatusFilter === 'all' && paymentTypeFilter === 'all' && !dateRange.from && !dateRange.to && (
                  <Button onClick={() => navigate('/sales/new')}>
                    <Plus className="w-4 h-4 mr-2" />
                    Satış Oluştur
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredSales.map((sale) => {
                  const statusConfig = paymentStatusConfig[sale.paymentStatus];
                  const typeConfig = sale.paymentType ? paymentTypeConfig[sale.paymentType] : null;
                  const TypeIcon = typeConfig?.icon;
                  const isOnHoldRecord = sale.isOnHold === true;
                  const isCancelled = sale.status === 'cancelled';
                  const isCompletedOrConvertedOnHold = sale.isCompletedOrConvertedOnHold === true;
                  
                  // Determine the actual sale ID for display and navigation
                  // Use actualSaleId if available, otherwise fall back to id
                  const displaySaleId = sale.actualSaleId || sale.id;
                  const navigateSaleId = sale.linkedSaleId || (sale.id.startsWith('onhold_') ? null : sale.id);
                  
                  // Color coding for completed/converted onhold records - using green/blue theme
                  const completedOnHoldBgColor = 'bg-green-50 dark:bg-green-950/30';
                  const completedOnHoldBorderColor = 'border-green-200 dark:border-green-800';
                  const completedOnHoldTextColor = 'text-green-600 dark:text-green-500';
                  const completedOnHoldIconBg = 'bg-green-100 dark:bg-green-900/30';
                  
                  // Color coding for regular onhold records - using orange/purple theme
                  const onHoldBgColor = 'bg-orange-50 dark:bg-orange-950/30';
                  const onHoldBorderColor = 'border-orange-200 dark:border-orange-800';
                  const onHoldTextColor = 'text-orange-600 dark:text-orange-500';
                  const onHoldIconBg = 'bg-orange-100 dark:bg-orange-900/30';
                  
                  // Color coding for cancelled sales
                  const cancelledBgColor = 'bg-destructive/5 dark:bg-destructive/10';
                  const cancelledBorderColor = 'border-destructive/20 dark:border-destructive/30';
                  const cancelledTextColor = 'text-destructive/70 dark:text-destructive/60';
                  const cancelledIconBg = 'bg-destructive/10 dark:bg-destructive/20';
                  
                  return (
                    <div
                      key={sale.id}
                      className={cn(
                        "flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl transition-all duration-200 border cursor-pointer",
                        isCompletedOrConvertedOnHold
                          ? `${completedOnHoldBgColor} ${completedOnHoldBorderColor} hover:bg-green-100/50 dark:hover:bg-green-950/50 border-2`
                          : isOnHoldRecord
                          ? `${onHoldBgColor} ${onHoldBorderColor} hover:bg-orange-100/50 dark:hover:bg-orange-950/50 border-2`
                          : isCancelled
                          ? `${cancelledBgColor} ${cancelledBorderColor} hover:bg-destructive/10 dark:hover:bg-destructive/20`
                          : "bg-card hover:bg-accent/5"
                      )}
                      onClick={() => {
                        if (isOnHoldRecord && navigateSaleId) {
                          // Onhold record linked to a sale - navigate to that sale
                          navigate(`/sales/${navigateSaleId}`);
                        } else if (isOnHoldRecord) {
                          // Onhold record without linked sale - navigate to onhold detail page
                          // Check if ID is already in onhold_ format
                          if (sale.id.startsWith('onhold_')) {
                            // Use the ID directly
                            navigate(`/onhold-detail/${sale.id}`);
                          } else if (sale.customerId && sale.originalIds && sale.originalIds.length > 0) {
                            // Grouped records - create composite ID: customerId-date
                            const dateStr = format(sale.date, 'yyyy-MM-dd');
                            const compositeId = `${sale.customerId}-${dateStr}`;
                            navigate(`/onhold-detail/${compositeId}`);
                          } else if (sale.originalIds && sale.originalIds.length > 0) {
                            // Fallback: use first original ID with prefix
                            navigate(`/onhold-detail/onhold_${sale.originalIds[0]}`);
                          } else {
                            navigate('/onhold-sale');
                          }
                        } else {
                          // Regular sale
                          navigate(`/sales/${sale.id}`);
                        }
                      }}
                    >
                      <div className={cn(
                        "w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0",
                        isCompletedOrConvertedOnHold
                          ? completedOnHoldIconBg
                          : isOnHoldRecord
                          ? onHoldIconBg
                          : isCancelled
                          ? cancelledIconBg
                          : "bg-primary/10"
                      )}>
                        <ShoppingCart className={cn(
                          "w-5 h-5 sm:w-6 sm:h-6",
                          isCompletedOrConvertedOnHold
                            ? completedOnHoldTextColor
                            : isOnHoldRecord 
                            ? onHoldTextColor 
                            : isCancelled
                            ? cancelledTextColor
                            : "text-primary"
                        )} />
                      </div>

                      <div className="flex-1 min-w-0 w-full sm:w-auto">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-2 sm:mb-1">
                          <h3 className={cn(
                            "font-semibold", 
                            isCompletedOrConvertedOnHold && completedOnHoldTextColor,
                            isOnHoldRecord && !isCompletedOrConvertedOnHold && onHoldTextColor
                          )}>
                            {sale.customerName}
                          </h3>
                          <div className="flex items-center gap-2">
                            {isCompletedOrConvertedOnHold && (
                              <Badge variant="outline" className="bg-green-500/10 text-green-600 dark:text-green-500 border-green-500/30 font-medium">
                                {sale.status === 'completed' ? 'Tamamlandı (On Hold)' : 'Dönüştürüldü (On Hold)'}
                              </Badge>
                            )}
                            {isOnHoldRecord && !isCompletedOrConvertedOnHold && (
                              <Badge variant="outline" className="bg-orange-500/10 text-orange-600 dark:text-orange-500 border-orange-500/30 font-medium">
                                Beklemede (On Hold)
                              </Badge>
                            )}
                            {isCancelled && (
                              <Badge variant="outline" className={saleStatusConfig.cancelled.color + ' ' + saleStatusConfig.cancelled.bgColor + ' ' + saleStatusConfig.cancelled.borderColor}>
                                {saleStatusConfig.cancelled.label}
                              </Badge>
                            )}
                            {!isOnHoldRecord && !isCancelled && (
                              <Badge variant="outline" className={statusConfig.color}>
                                {statusConfig.label}
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        {/* Product names */}
                        {sale.productNames.length > 0 && (
                          <div className="mb-1">
                            <span className="text-sm text-muted-foreground">
                              {sale.productNames.join(', ')}
                              {sale.lineCount > sale.productNames.length && ` +${sale.lineCount - sale.productNames.length} daha`}
                            </span>
                          </div>
                        )}
                        
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs sm:text-sm text-muted-foreground">
                          <span>#{displaySaleId.slice(-8)}</span>
                          {sale.linkedSaleId && (isOnHoldRecord || isCompletedOrConvertedOnHold) && (
                            <span className="text-xs">(Bağlı: #{sale.linkedSaleId.slice(-8)})</span>
                          )}
                          <span>{sale.customerPhone}</span>
                          <span>{format(sale.date, 'MMM d, yyyy')}</span>
                          {sale.paymentType && typeConfig && !isOnHoldRecord && (
                            <div className="flex items-center gap-1">
                              <TypeIcon className="w-3 h-3" />
                              <span>{typeConfig.label}</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          <span>{sale.lineCount} ürün</span>
                          {sale.productNames.length > 0 && sale.lineCount > sale.productNames.length && (
                            <span className="text-muted-foreground">• {sale.productNames.length} gösteriliyor</span>
                          )}
                          {!isOnHoldRecord && (
                            <>
                              <span>•</span>
                              <span>Satan: {sale.salesPerson}</span>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3 w-full sm:w-auto">
                        <div className="flex flex-col sm:text-right flex-1">
                          <div className={cn(
                            "font-semibold text-lg",
                            isCompletedOrConvertedOnHold && completedOnHoldTextColor,
                            isOnHoldRecord && !isCompletedOrConvertedOnHold && onHoldTextColor,
                            isCancelled && cancelledTextColor
                          )}>
                            ₺{sale.total.toLocaleString('tr-TR')}
                          </div>
                          <div className="text-xs text-muted-foreground">Toplam</div>
                        </div>

                        <Button variant="ghost" size="sm">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
                
                {/* Pagination Controls - Only show when searching */}
                {searchTerm.trim() && (
                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="text-sm text-muted-foreground">
                      {totalResults > 0 ? (
                        <>
                          Sayfa {currentPage} - {Math.min(currentPage * pageSize, totalResults)} / {totalResults} sonuç gösteriliyor
                        </>
                      ) : (
                        'Sonuç bulunamadı'
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (currentPage > 1) {
                            setCurrentPage(currentPage - 1);
                          }
                        }}
                        disabled={currentPage === 1 || loading}
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Önceki
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (hasMoreResults) {
                            setCurrentPage(currentPage + 1);
                          }
                        }}
                        disabled={!hasMoreResults || loading}
                      >
                        Sonraki
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}